import { useState } from "react";
import type { Database } from "@/lib/supabase";
import { Badge } from "../ui/badge";
import { getDisplayName, formatLastMessageDate, getContactAvatarUrl } from "@/utils/contactUtils";
import clsx from "clsx";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

interface ContactListItemProps {
  contact: AccountContact;
  isSelected: boolean;
  isUnread?: boolean;
  onSelect: () => void;
}

export const ContactListItem = ({
  contact,
  isSelected,
  isUnread = false,
  onSelect,
}: ContactListItemProps) => {
  const [isClicking, setIsClicking] = useState(false);
  const [imageError, setImageError] = useState(false);
  const displayName = getDisplayName(contact);
  const lastMessageDate = formatLastMessageDate(contact.last_message_at);
  const avatarUrl = getContactAvatarUrl(contact);
  const tags: string[] = (contact.tags as string[]) || [];

  const handleClick = () => {
    if (isClicking) return;
    setIsClicking(true);
    onSelect();
    setTimeout(() => setIsClicking(false), 100);
  };

  return (
    <div
      onClick={handleClick}
      className={clsx(
        "relative px-3 py-2.5 cursor-pointer transition-colors duration-200 border-b border-border dark:border-[#3F3F464D] last:border-b-0",
        "text-foreground dark:text-neutral-50",
        isSelected
          ? "bg-[#EEF4FF] dark:bg-[#3f3f46]"
          : "hover:bg-[#F8FAFC] dark:hover:bg-[#27272a]",
        isClicking && "opacity-70"
      )}
    >
      {isSelected && (
        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#2563EB] rounded-r-sm" />
      )}
      <div className="flex items-center gap-2">
        <div className="relative w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
          <img
            src={
              imageError
                ? getContactAvatarUrl({ ...contact, profile_picture: null })
                : avatarUrl
            }
            alt={displayName}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3
              className={clsx(
                "truncate text-[13px]",
                isUnread ? "font-semibold" : "font-medium"
              )}
            >
              {displayName}
            </h3>
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
              <span
                className={clsx(
                  "text-[11px]",
                  isUnread
                    ? "text-foreground/70 dark:text-neutral-200"
                    : "text-muted-foreground dark:text-neutral-400"
                )}
              >
                {lastMessageDate}
              </span>
              {isUnread && (
                <span className="w-2 h-2 rounded-full bg-[#2563EB] flex-shrink-0" />
              )}
            </div>
          </div>
          {tags.length > 0 && (
            <div className="flex gap-1 mt-0.5 flex-wrap">
              {tags.slice(0, 3).map((tag) => (
                <Badge
                  key={tag}
                  variant="tag"
                  className="text-[10px] !px-2 !py-0.5 leading-4 !rounded-lg"
                >
                  {tag}
                </Badge>
              ))}
              {tags.length > 3 && (
                <span className="text-[10px] text-muted-foreground dark:text-neutral-400 leading-4">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
